import Alpine from 'alpinejs'

/**
 * Location Map Component
 *
 * Alpine.js component for displaying Google Maps with polling location markers.
 * Dynamically loads Google Maps API and initializes map with markers for each location.
 */
export default ({
  locations,
  center,
  mapId,
}) => ({
  locations: Array.isArray(locations) ? locations : [],
  center: center || null,
  mapId: typeof mapId === 'string' && mapId.trim() ? mapId.trim() : null,
  map: null,
  mapContainer: null,
  markerMap: {},
  activeInfoWindow: null,
  hasMap: false,
  mapLoaded: false,
  mapError: '',

  init() {
    // Check if map container exists
    this.mapContainer = this.$refs.mapContainer
    if (!this.mapContainer) {
      // Component works without map so no error message is needed. It will just be a list of locations.
      this.hasMap = false
      return
    }

    if (!Array.isArray(this.locations) || this.locations.length === 0) {
      this.mapError = 'No locations available'
      return
    }

    if (!this.center || typeof this.center.lat !== 'number' || typeof this.center.lng !== 'number') {
      this.mapError = 'Invalid map center'
      return
    }

    this.hasMap = true

    const initialize = () => {
      if (this.mapLoaded) {
        return
      }

      this.initializeMap(this.locations)
    }

    if (window.googleMapsLoaded) {
      initialize()
    } else {
      window.addEventListener('googlemapsloaded', initialize, { once: true })
    }
  },

  initializeMap(locations) {
    this.map = this.createMapInstance(this.mapContainer, this.center)

    const bounds = new google.maps.LatLngBounds()

    locations.forEach((location, index) => {
      this.createLocationMarker(location, bounds, index)
    })

    this.fitMapToBounds(bounds, locations.length)
    this.mapLoaded = true
  },

  createMapInstance(container, center) {
    const mapOptions = {
      center: center,
      zoom: 12,
      mapId: this.mapId || undefined,
      mapTypeControl: false,
      fullscreenControl: true,
      streetViewControl: false,
    }

    // Google disallows inline styles when a mapId is present.
    if (!this.mapId) {
      mapOptions.styles = [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ]
    }

    return new google.maps.Map(container, mapOptions)
  },

  buildInfoWindowContent(location) {
    const title = location.title || 'Location'
    const address1 = location.address1 || ''
    const address2 = location.address2 || ''
    const city = location.city || ''
    const state = location.state || ''
    const zip = location.zipCode || ''
    const phoneNumber = location.phoneNumber || ''
    const email = location.email || ''

    const cityStateZipParts = [city, state, zip].filter(part => part !== '')
    const cityStateZip = cityStateZipParts.join(', ')

    const fullAddressParts = [address1, address2, city, state, zip].filter(part => part !== '')
    const fullAddressString = fullAddressParts.join(' ')

    const mapsUrl = fullAddressString
      ? 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(fullAddressString)
      : null

    return `
      <div style="padding: 8px; min-width: 200px;">
        <h3 style="margin: 0 28px 8px 0; font-weight: bold; font-size: 16px; color: #002868;">
          ${title}
        </h3>
        <p style="margin: 0; font-size: 14px; color: #002868; line-height: 1.5;">
          ${
  mapsUrl
    ? `<a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">
                  ${address1 ? address1 + '<br>' : ''}
                  ${address2 ? address2 + '<br>' : ''}
                  ${cityStateZip}
                </a>`
    : ''
  }
          ${ phoneNumber || email ? '<br>' : ''}
          ${phoneNumber ? phoneNumber + '<br>' : ''}
          ${email ? '<a style="color: #002868; text-decoration: underline;" href="mailto:' + email + '">' + email + '</a>' : ''}
        </p>
      </div>
    `
  },

  createLocationMarker(location, bounds, index) {
    const position = { lat: location.lat, lng: location.lng }
    bounds.extend(position)

    const title = location.title || 'Location'

    const infoWindow = new google.maps.InfoWindow({
      content: this.buildInfoWindowContent(location),
    })

    const marker = this.createMarker(position, title, () => {
      this.openInfoWindow(infoWindow, marker)
    })

    // Store marker by location ID for card clicks
    if (location.id) {
      this.markerMap[location.id] = { marker, infoWindow }
    }

    if (index === 0) {
      this.openInfoWindow(infoWindow, marker)
    }
  },

  createMarker(position, title, onClick) {
    // Advanced markers silently render nothing without a Map ID, so only use
    // them when one is configured and fall back to a classic Marker otherwise.
    if (this.mapId && google.maps.marker?.AdvancedMarkerElement) {
      const markerPin = new google.maps.marker.PinElement({
        background: '#dd454c',
        borderColor: '#ffffff',
        glyphColor: '#ffffff',
        scale: 1,
      })

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: position,
        map: this.map,
        title: title,
        content: markerPin,
      })

      marker.addEventListener('gmp-click', onClick)
      return marker
    }

    const marker = new google.maps.Marker({
      position: position,
      map: this.map,
      title: title,
      icon: {
        path: 'M12 0C7.03 0 3 4.03 3 9c0 6.75 9 15 9 15s9-8.25 9-15c0-4.97-4.03-9-9-9z',
        fillColor: '#dd454c',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 1.5,
        scale: 1.4,
        anchor: new google.maps.Point(12, 24),
      },
    })

    marker.addListener('click', onClick)
    return marker
  },

  openInfoWindow(infoWindow, marker) {
    if (this.activeInfoWindow) {
      this.activeInfoWindow.close()
    }
    infoWindow.open({
      anchor: marker,
      map: this.map,
    })
    this.activeInfoWindow = infoWindow
  },

  focusLocationById(locationId) {
    if (!this.hasMap) {
      return
    }

    const entry = this.markerMap[locationId]
    if (!entry) {
      return
    }

    this.mapContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
    this.mapContainer.focus()

    // AdvancedMarkerElement exposes .position; classic Marker uses getPosition()
    const position = entry.marker?.position ?? entry.marker?.getPosition?.()
    if (position) {
      this.map.panTo(position)
    }
    this.openInfoWindow(entry.infoWindow, entry.marker)
  },

  fitMapToBounds(bounds, locationCount) {
    if (locationCount > 1) {
      this.map.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50,
      })
    } else {
      this.map.setZoom(14)
    }
  },
})
